import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { dealerService } from '../services/api';
import { MapPin, Phone, Briefcase, Search, AlertCircle, Navigation, ExternalLink, X, Mail } from 'lucide-react';
import { updateMetaTags } from '../utils/seo';

const DEFAULT_RADIUS = 25;
const geolocationSupported = typeof window !== 'undefined' && 'geolocation' in navigator;
const exampleSearches = ['Mumbai', 'Hyderabad', 'North Zone', 'Institutional'];

const dealerIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

function DealerLocator() {
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [radius, setRadius] = useState(DEFAULT_RADIUS);
  const [coords, setCoords] = useState(null);
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [error, setError] = useState('');
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    updateMetaTags({
      title: 'Find a Dealer',
      description: 'Search across our pan-India network of dealers and distribution partners. Use GPS to discover the closest experience centre.'
    });
  }, []);

  useEffect(() => {
    const lat = searchParams.get('lat');
    const lng = searchParams.get('lng');
    const rad = searchParams.get('radius');
    if (lat && lng) {
      setCoords({ latitude: Number(lat), longitude: Number(lng) });
    }
    if (rad) {
      setRadius(Number(rad));
    }
  }, [searchParams]);

  useEffect(() => {
    loadDealers();
  }, [coords, radius, featuredOnly]);

  const loadDealers = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        active: 1,
        search,
      };
      if (featuredOnly) params.featured = 1;
      if (coords) {
        params.latitude = coords.latitude;
        params.longitude = coords.longitude;
        params.radius = radius;
      }
      const data = await dealerService.index(params);
      const list = data.data || data || [];
      setDealers(list);
    } catch (err) {
      console.error('Error loading dealers:', err);
      setError('Unable to load dealers at the moment.');
    } finally {
      setLoading(false);
    }
  };

  const requestLocation = () => {
    if (!geolocationSupported) {
      setError('Geolocation is not supported on this device.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setCoords({ latitude, longitude });
        setSearchParams({ lat: latitude, lng: longitude, radius });
      },
      () => setError('Unable to fetch your location. Please enable GPS and try again.'),
      { enableHighAccuracy: true }
    );
  };

  const filtered = useMemo(() => {
    return (dealers.data || dealers).filter((d) => {
      const term = search.toLowerCase();
      return (
        (d.name || '').toLowerCase().includes(term) ||
        (d.city || '').toLowerCase().includes(term) ||
        (d.state || '').toLowerCase().includes(term)
      );
    });
  }, [dealers, search]);

  const center = coords || { latitude: filtered[0]?.latitude || 20.5937, longitude: filtered[0]?.longitude || 78.9629 };

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="bg-slate-900 py-20 px-4">
        <div className="max-w-7xl mx-auto text-center space-y-6">
          <p className="text-red-400 font-bold tracking-[0.3em] uppercase text-sm">Experience Centre</p>
          <h1 className="text-4xl md:text-6xl font-black text-white">Find a Partner Near You</h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">Explore our premium selection at authorized Ortus dealers. Use your location to find the closest distribution point.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 -mt-10 pb-20 space-y-12">
        <div className="bg-white rounded-[2rem] shadow-2xl shadow-slate-200/50 border border-slate-100 overflow-hidden grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-4 p-8 lg:p-10 border-r border-slate-100 space-y-8 bg-slate-50/50">
            <div className="space-y-4">
              <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Global Search</label>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input 
                  value={search} 
                  onChange={(e) => setSearch(e.target.value)} 
                  placeholder="City, State, or Dealer Name" 
                  className="w-full rounded-2xl border border-slate-200 pl-11 pr-4 py-3.5 text-sm focus:outline-none focus:border-red-500 transition-all shadow-sm bg-white" 
                />
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                {exampleSearches.map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => setSearch(chip)}
                    className="text-[10px] font-bold px-3 py-1.5 rounded-full border border-slate-200 text-slate-500 hover:border-red-500 hover:text-red-600 hover:bg-red-50 transition-all"
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4 pt-6 border-t border-slate-200">
              <div className="flex justify-between items-center">
                <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Search Radius</label>
                <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-lg">{radius} km</span>
              </div>
              <input 
                type="range" 
                min="5" 
                max="200" 
                value={radius} 
                onChange={(e) => setRadius(Number(e.target.value))} 
                className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-red-500" 
              />
            </div>

            <div className="flex items-center gap-3 py-2">
              <div className="relative inline-flex items-center cursor-pointer">
                <input id="featured" type="checkbox" checked={featuredOnly} onChange={(e) => setFeaturedOnly(e.target.checked)} className="sr-only peer" />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
                <label htmlFor="featured" className="ms-3 text-sm font-medium text-slate-700">Featured partners only</label>
              </div>
            </div>

            <div className="space-y-3 pt-6">
              <button 
                onClick={requestLocation} 
                className="w-full bg-slate-900 text-white font-bold py-4 rounded-2xl hover:bg-slate-800 transition-all shadow-lg flex items-center justify-center gap-2"
              >
                <MapPin size={18} className="text-red-400" />
                Use Current Location
              </button>
              {error && (
                <div className="p-3 bg-red-50 border border-red-100 rounded-xl flex items-start gap-2">
                  <AlertCircle size={14} className="text-red-500 mt-0.5 shrink-0" />
                  <p className="text-[11px] text-red-600 font-medium leading-relaxed">{error}</p>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-8 h-[500px] lg:h-auto bg-slate-100 overflow-hidden relative">
            {!loading && (
              <MapContainer
                center={[center.latitude, center.longitude]}
                zoom={coords ? 9 : 5}
                scrollWheelZoom={false}
                style={{ height: '100%', width: '100%' }}
                className="z-10"
              >
                <TileLayer
                  attribution="&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a>"
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {filtered.map((dealer) => (
                  dealer.latitude && dealer.longitude ? (
                    <Marker key={dealer.id} position={[dealer.latitude, dealer.longitude]} icon={dealerIcon}>
                      <Popup className="custom-popup">
                        <div className="p-1 space-y-2">
                          <h4 className="font-bold text-slate-900 border-b border-slate-100 pb-2">{dealer.name}</h4>
                          <div className="text-xs text-slate-600 flex items-center gap-1.5">
                            <MapPin size={10} className="text-red-500" />
                            {dealer.city}, {dealer.state}
                          </div>
                          {dealer.phone && (
                            <div className="text-xs text-slate-600 flex items-center gap-1.5">
                              <Phone size={10} className="text-red-500" />
                              {dealer.phone}
                            </div>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  ) : null
                ))}
              </MapContainer>
            )}
            {loading && (
              <div className="absolute inset-0 bg-slate-100/50 backdrop-blur-sm z-20 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-10 h-10 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-xs font-black uppercase tracking-widest text-slate-500">Locating Partners...</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="flex items-end justify-between border-b border-slate-200 pb-4">
            <div>
              <p className="text-[10px] uppercase font-black tracking-[0.3em] text-slate-400 mb-1">Partner List</p>
              <h2 className="text-3xl font-black text-slate-900">{filtered.length} Dealers Found</h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pt-4">
            {filtered.map((dealer) => (
              <div 
                key={dealer.id} 
                onClick={() => setSelectedDealer(dealer)}
                className="group p-8 rounded-[2rem] border border-slate-100 shadow-xl shadow-slate-200/50 hover:border-red-500/30 transition-all duration-300 bg-white flex flex-col h-full cursor-pointer hover:-translate-y-1"
              >
                <div className="flex justify-between items-start mb-6">
                  {dealer.logo ? (
                    <img src={dealer.logo} alt={dealer.name} className="h-14 w-14 rounded-2xl object-cover border border-slate-100" />
                  ) : (
                    <div className="bg-red-50 p-3 rounded-2xl text-red-600 group-hover:bg-red-600 group-hover:text-white transition-all duration-300">
                      <Briefcase size={20} />
                    </div>
                  )}
                  {dealer.is_featured && (
                    <span className="text-[10px] font-black uppercase tracking-widest bg-red-500 text-white px-3 py-1.5 rounded-full shadow-lg shadow-red-500/20">
                      Top Rated
                    </span>
                  )}
                </div>
                
                <h3 className="text-2xl font-bold text-slate-900 mb-2 group-hover:text-red-700 transition-colors">{dealer.name}</h3>
                <p className="text-red-600 font-bold text-sm mb-6 flex items-center gap-2">
                  <MapPin size={14} />
                  {dealer.city}, {dealer.state}
                </p>
                
                {dealer.distance && (
                  <div className="mb-6 bg-slate-50 px-4 py-2 rounded-xl border border-slate-100 inline-flex items-center gap-2 text-xs font-bold text-slate-500">
                    <Navigation size={12} className="text-red-500" />
                    {dealer.distance} km from you
                  </div>
                )}
                
                <div className="space-y-4 pt-6 border-t border-slate-100 mt-auto">
                  <div className="space-y-2">
                    <p className="text-xs text-slate-500 font-medium leading-relaxed">{dealer.address}</p>
                    {dealer.phone && (
                      <div className="flex items-center gap-2 text-slate-700 font-bold text-sm">
                        <Phone size={14} className="text-slate-400" />
                        {dealer.phone}
                      </div>
                    )}
                  </div>
                  
                  {dealer.website && (
                    <a 
                      href={dealer.website.startsWith('http') ? dealer.website : `https://${dealer.website}`}
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-flex items-center gap-2 text-sm font-black uppercase tracking-widest text-red-600 hover:text-red-700 transition-colors group/link"
                    >
                      Visit Experience Centre
                      <ExternalLink size={14} className="group-hover/link:translate-x-1 transition-transform" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {filtered.length === 0 && !loading && (
            <div className="text-center py-32 bg-white rounded-[2rem] border-2 border-dashed border-slate-200">
              <div className="bg-slate-50 w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 text-slate-300">
                <Search size={40} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">No partners found</h3>
              <p className="text-slate-400 max-w-sm mx-auto">Try expanding your search radius or searching for a different city.</p>
            </div>
          )}
        </div>
      </div>

      {selectedDealer && (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md flex items-center justify-center z-[110] p-4">
          <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
            <div className="relative h-48 bg-slate-900">
              <button 
                onClick={() => setSelectedDealer(null)} 
                className="absolute top-6 right-6 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all z-10"
              >
                <X size={20} />
              </button>
              
              <div className="absolute -bottom-12 left-10">
                <div className="h-32 w-32 rounded-[2rem] bg-white p-1 shadow-2xl border-4 border-white">
                  {selectedDealer.logo ? (
                    <img src={selectedDealer.logo} alt={selectedDealer.name} className="h-full w-full object-cover rounded-[1.75rem]" />
                  ) : (
                    <div className="h-full w-full bg-slate-100 rounded-[1.75rem] flex items-center justify-center text-red-600 text-4xl font-black">
                      {selectedDealer.name?.charAt(0)}
                    </div>
                  )}
                </div>
              </div>

              {selectedDealer.is_featured && (
                <div className="absolute top-10 left-10 px-4 py-1.5 rounded-full bg-red-500 text-white text-[10px] font-black uppercase tracking-[0.2em] shadow-lg shadow-red-500/30">
                  Premium Certified Partner
                </div>
              )}
            </div>

            <div className="px-10 pt-16 pb-12 space-y-8">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-3xl font-black text-slate-900">{selectedDealer.name}</h3>
                  <div className="flex items-center gap-2 mt-2 text-red-600 font-bold">
                    <MapPin size={16} />
                    <span>{selectedDealer.city}, {selectedDealer.state}</span>
                  </div>
                </div>
                {selectedDealer.website && (
                  <a 
                    href={selectedDealer.website.startsWith('http') ? selectedDealer.website : `https://${selectedDealer.website}`}
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-2xl font-bold hover:bg-red-700 transition-all text-sm shadow-xl shadow-red-500/20"
                  >
                    Visit Experience Centre
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div className="space-y-6">
                  <div>
                    <label className="text-[10px] uppercase font-black tracking-widest text-slate-400 block mb-2">Location Details</label>
                    <p className="text-slate-600 text-sm leading-relaxed font-medium">{selectedDealer.address}, {selectedDealer.postal_code}</p>
                    {selectedDealer.opening_hours && (
                      <div className="mt-4 flex items-center gap-2 text-slate-500 text-xs font-bold bg-slate-50 p-3 rounded-xl border border-slate-100">
                        <Navigation size={12} className="text-red-500" />
                        Hours: {selectedDealer.opening_hours}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="text-[10px] uppercase font-black tracking-widest text-slate-400 block mb-2">Get in Touch</label>
                    <div className="space-y-3">
                      <a href={`mailto:${selectedDealer.email}`} className="flex items-center gap-3 text-slate-700 text-sm font-bold hover:text-red-600 transition-colors">
                        <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400">
                          <Mail size={14} />
                        </div>
                        {selectedDealer.email}
                      </a>
                      <a href={`tel:${selectedDealer.phone}`} className="flex items-center gap-3 text-slate-700 text-sm font-bold hover:text-red-600 transition-colors">
                        <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400">
                          <Phone size={14} />
                        </div>
                        {selectedDealer.phone}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {selectedDealer.services && selectedDealer.services.length > 0 && (
                <div className="pt-4">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-400 block mb-4">Certified Services</label>
                  <div className="flex flex-wrap gap-2">
                    {selectedDealer.services.map((service, idx) => (
                      <span key={idx} className="px-4 py-2 rounded-xl bg-slate-50 border border-slate-100 text-slate-600 text-[11px] font-bold uppercase tracking-wider">
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-8 border-t border-slate-100 flex justify-center">
                <p className="text-[10px] uppercase font-black tracking-widest text-slate-400">Official Ortus FMCG Authorized Network Partner</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DealerLocator;
